// Small hook that tracks the current Supabase auth session and restores it on
// load / across refreshes. Also surfaces the one-time PASSWORD_RECOVERY event so
// the UI can show the "set a new password" screen after a reset link is opened.
//
// A Google sign-in comes back as ?code= on the URL and has to be exchanged for a
// session before there is anything to read. That exchange happens in
// lib/authCallback (once per page load, wherever it is called from) and is
// awaited here BEFORE getSession, so the first read cannot race it and report a
// signed-out user who is in fact signed in.
import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { completeAuthCallback } from "./authCallback";

export function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    let active = true;
    // Subscribed FIRST. The exchange below saves the session and notifies
    // subscribers as part of the same call, so listening afterwards could miss
    // the event it raises — including PASSWORD_RECOVERY.
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (!active) return;
      setSession(s);
      if (s) setAuthError("");
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
    });
    // Finish an OAuth/recovery return, then read the session. Shared and
    // idempotent: whoever gets here first performs the one exchange, and it is
    // the only place in the app that exchanges a code.
    completeAuthCallback()
      .then((r) => {
        if (!active || !r || !r.handled) return;
        if (!r.ok && r.message) setAuthError(r.message);
        // A recovery link signs the user in so they can set a new password. The
        // exchange tells us which kind of return this was, so the reset screen
        // opens whether or not the event was observed.
        if (r.ok && r.recovery) setRecovery(true);
      })
      .catch(() => {})
      // getSession runs only after the exchange, so it cannot read "signed out"
      // a moment before the session is saved — and it cannot overwrite it: the
      // exchange has already persisted the session it returns.
      .then(() => supabase.auth.getSession())
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session || null);
        setLoading(false);
      })
      .catch(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    user: (session && session.user) || null,
    accessToken: (session && session.access_token) || null,
    loading,
    recovery,
    authError,
    clearAuthError: () => setAuthError(""),
    clearRecovery: () => setRecovery(false),
  };
}
